<script setup lang="ts">
import { MateInteractionStatus } from "@/enums/mission";
import { useGlobalModal } from "@/stores/globalModal";
import { useGlobalUser } from "@/stores/globalUser";
import { useInteractive } from "@/stores/interactive";
import { useInteractiveReview } from "@/stores/interactive-review";
import { useMate } from "@/stores/mate";
import { useNotification } from "@/stores/notification";
import { useUI } from "@/stores/ui";
import { sessionStorageSet } from "@/tools/sessionStorage";
import { InteractionPool, Mate } from "@/types/entity";
import { useDefaultAvatar, useImgWithMediumSize } from "@/use/common";
import { showConfirmDialog } from "vant";
import { onActivated, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { RouteLocation, RouteLocationRaw, useRouter } from "vue-router";
import { useHouseSession } from "../../store/houseSession";

const user = useGlobalUser();
const mateStore = useMate();
const interactiveStore = useInteractive();
const router = useRouter();
const reviewStore = useInteractiveReview();
const notification = useNotification();
const { t } = useI18n();
const houseStore = useHouseSession();

const onMateItemClicked = async (mate: Mate, to: RouteLocationRaw) => {
  if (!user.isLogin) return user.openLoginModal("register");

  // if (mateStore.currentMate?.id !== mate.id)
  //   await mateStore.changeCurrentMate(mate.id);
  // 2. 当前路由允许Mate切换：直接切换

  if ((to as RouteLocation).name === "missionHome") {
    sessionStorageSet("mission-home:reload", 1);
  }

  router.push(to);
};

const toggleUserInteractionPoolStatus = async () => {
  await user.updateUserApi({
    interactionPoolStatus: user.user.interactionPoolStatus === 1 ? 2 : 1,
  });
};

const modalStore = useGlobalModal();

const uiStore = useUI();
const clickAddMate = () => {
  uiStore.popInteractivePoolMateSelector();
};

const removeMate = (pool: InteractionPool) => {
  showConfirmDialog({
    message: t("Message.removeFromPool", {
      name: pool.mate?.name,
    }),
    confirmButtonText: t("Buttons.confirm"),
    cancelButtonText: t("Buttons.cancel"),
  }).then(async () => {
    await interactiveStore.removeFromPool(pool.id);
    reviewStore.initOrFetchList(null, true);
  });
};

const clickPoolMate = (pool: InteractionPool) => {
  modalStore.openGlobalActionSheet([
    {
      name: t("interactive.goToOC"),
      callback: () => {
        router.push({
          name: "mateHome",
          params: { uuid: pool.mate?.uuid },
        });
      },
    },
    {
      name: t("Buttons.editProfile"),
      callback: () => {
        router.push({
          name: "mateEdit",
          params: { uuid: pool.mate?.uuid },
        });
      },
    },
    {
      name: t("interactive.viewMemory"),
      callback: () => {
        router.push({
          name: "mateMemory",
          query: { mateUuid: pool.mate?.uuid },
        });
      },
    },
    {
      name: t("interactive.removeFromPool"),
      className: "text-error",
      callback: () => {
        removeMate(pool);
      },
    },
  ]);
};

onMounted(() => {
  interactiveStore.getPoolList();
});

onActivated(() => {
  interactiveStore.getPoolList();
  notification.initSummaryCount();
});
</script>
<template>
  <div class="w-full h-full overflow-hidden grid grid-rows-[auto_1fr]">
    <div class="w-full overflow-hidden">
      <div class="red-dot ml-5 mr-2 flex items-center mt-3 mb-2">
        <div class="mr-2 font-semibold">
          {{ $t("interactive.ocInteractionPool") }}
        </div>
        <div class="flex items-center gap-1 flex-1 text-xs">
          <div
            :class="[
              'w-2 h-2  rounded-full',
              user.user.interactionPoolStatus === 2 ? 'bg-error' : 'bg-success',
            ]"
          />
          <p>
            {{
              user.user.interactionPoolStatus === 2
                ? $t("interactive.stopped")
                : $t("interactive.running")
            }}
          </p>
          <i
            @click="toggleUserInteractionPoolStatus"
            :class="[
              'iconify w-4',
              user.user.interactionPoolStatus === 1
                ? 'ph--pause-circle-bold'
                : 'ph--play-circle-bold',
            ]"
          />
        </div>
        <div
          v-if="user.isLogin"
          class="flex items-center gap-1 text-xs font-normal"
        >
          <template v-if="!user.isFreeModel()">
            {{ $t("interactive.twentyFourHourEstimatedConsumption") }}
            <PowerIcon />
            {{
              user.getPowerCostByModel(
                interactiveStore.estimatedPowerCubes || 0
              )
            }}
          </template>
          <button
            class="btn btn-xs rounded"
            @click="houseStore.showHouseSetting = true"
          >
            <i class="iconify mingcute--settings-3-line w-4"></i>
          </button>
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <VanRow gutter="5" class="px-2">
          <VanCol :span="8">
            <button
              class="btn btn-sm gap-y-1 indicator flex-col w-full rounded"
              @click="
                onMateItemClicked(mateStore.userMate, {
                  name: 'missionHome',
                })
              "
            >
              <span
                class="indicator-item badge badge-primary rounded-full badge-xs text-primary-content p-0 w-5 h-5"
                v-if="
                  notification.interactionSummary
                    ?.awaitingReceptionDispatchCount
                "
              >
                {{
                  notification.interactionSummary
                    ?.awaitingReceptionDispatchCount
                }}
              </span>
              <i class="iconify fluent--important-16-regular"></i>
              {{ $t("interactive.dispatch") }}
            </button>
          </VanCol>
          <VanCol :span="8">
            <button
              class="btn btn-sm gap-y-1 indicator flex-col w-full rounded"
              @click="
                onMateItemClicked(mateStore.userMate, {
                  name: 'relationshipList',
                })
              "
            >
              <i class="iconify fluent--connected-16-regular"></i>
              {{ $t("interactive.relationshipNetwork") }}
            </button>
          </VanCol>
          <VanCol :span="8">
            <button
              class="btn btn-sm gap-y-1 indicator flex-col w-full rounded"
              @click="
                onMateItemClicked(mateStore.userMate, {
                  name: 'interactionList',
                })
              "
            >
              <span
                class="indicator-item badge badge-primary rounded-full badge-xs text-primary-content p-0 w-5 h-5"
                v-if="notification.displayInteractionPoolSummary"
              >
                {{ notification.displayInteractionPoolSummary }}
              </span>
              <i class="iconify mingcute--notebook-2-line" />
              {{ $t("Buttons.interactionLog") }}
            </button>
          </VanCol>
        </VanRow>
        <VanRow gutter="5" class="px-2">
          <VanCol :span="8">
            <button
              class="btn btn-sm gap-y-1 indicator flex-col w-full rounded"
              @click="
                onMateItemClicked(mateStore.userMate, {
                  name: 'mailList',
                })
              "
            >
              <span
                class="indicator-item badge badge-primary rounded-full badge-xs text-primary-content p-0 w-5 h-5"
                v-if="notification.interactionSummary?.unreadMailCount"
              >
                {{ notification.interactionSummary?.unreadMailCount }}
              </span>
              <i class="iconify fluent--mail-16-regular"></i>
              {{ $t("interactive.mailbox") }}
            </button>
          </VanCol>
          <VanCol :span="8">
            <button
              class="btn btn-sm gap-y-1 indicator flex-col w-full rounded"
              @click="
                onMateItemClicked(mateStore.userMate, {
                  name: 'missionJourney',
                })
              "
            >
              <i class="iconify fluent--book-16-regular"></i>
              {{ $t("interactive.handbook") }}
            </button>
          </VanCol>
          <VanCol :span="8">
            <button
              class="btn btn-sm gap-y-1 indicator flex-col w-full rounded"
              @click="
                onMateItemClicked(mateStore.userMate, {
                  name: 'missionEquipment',
                })
              "
            >
              <i class="iconify fluent--box-16-regular"></i>
              {{ $t("interactive.goods") }}
            </button>
          </VanCol>
        </VanRow>
      </div>
      <div class="px-2 flex overflow-hidden w-full pt-2 gap-1.5">
        <div class="flex overflow-x-auto flex-1 gap-1">
          <div
            class="flex flex-col gap-1 relative"
            v-for="pool in interactiveStore.poolList"
            :key="pool.id"
            @click="clickPoolMate(pool)"
          >
            <div
              class="w-10 aspect-[3/4] border rounded flex justify-center items-center overflow-hidden"
            >
              <img
                :src="
                  useImgWithMediumSize(
                    pool.sprite?.imageMedia?.url ||
                      pool.mate?.mediaUrl ||
                      useDefaultAvatar(),
                    72
                  )
                "
                alt=""
                :class="[
                  'h-full object-cover rounded',
                  pool.sprite?.imageMedia?.url ? '-mt-1' : '',
                ]"
                crossorigin="anonymous"
              />
            </div>
            <p
              class="text-[8px] text-center absolute bottom-0 left-0 right-0 badge badge- z-[1] badge-xs badge-warning whitespace-nowrap p-0 w-full"
              v-if="pool.mate?.interactionStatus !== MateInteractionStatus.Idle"
            >
              {{ $t(`mission.statusMap.${pool.mate?.interactionStatus}`) }}
            </p>
          </div>
        </div>
        <div class="flex flex-col gap-1" @click="clickAddMate">
          <div
            class="w-10 aspect-[3/4] border text-base-content/50 border-base-content/50 border-dashed flex justify-center items-center rounded"
          >
            <i class="iconify mingcute--add-circle-line" />
          </div>
        </div>
      </div>
    </div>
    <div class="w-full h-full overflow-y-scroll"></div>

    <HomeInteractiveHouseSetting v-model:show="houseStore.showHouseSetting" />
  </div>
</template>
